import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { checkUserFromRole } from "@/helpers";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import config from "@/lib/constants/config";
import { showToast } from "@/lib/utils/toast";
import { PATHS } from "@/lib/router";
import { isDevHost } from "@/lib/utils/isDevHost";
import { useAuthStore } from "@/features/auth/hooks";
import { useOnboardingStore } from "../hooks";
import { onboardingService } from "../services";
import { EsgForm, NxmapForm, NxplanForm } from "../components";

const getProjectFromLocation = (location, projectParam) => {
  const fromQuery = location.search
    ? new URLSearchParams(location.search).get("project")
    : "";
  return (projectParam || fromQuery || "").toLowerCase();
};

const buildVisitUrl = (projectName) => {
  const base = String(config.apiUrl || "").replace(/\/$/, "");
  const redirectPath = "/";
  return `${base}/visit/${projectName}?redirect=${encodeURIComponent(redirectPath)}`;
};

const OnboardingPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const location = useLocation();
  const { project: projectParam } = useParams();

  const project = useMemo(
    () => getProjectFromLocation(location, projectParam),
    [location, projectParam],
  );

  const { onboardData, loading, fetchOnboard, saveOnboard } =
    useOnboardingStore();

  const { user, userRole } = useAuthStore();

  const isUserLevel = checkUserFromRole(userRole);
  const permissions = user?.permissions || [];

  const hasCustomRoleName =
    typeof user?.customRoleName === "string" && user.customRoleName.trim();
  const canVisitProjects =
    (userRole || user?.role || "").toString().trim().toLowerCase() ===
      "super_admin" && !hasCustomRoleName;

  const [submitTick, setSubmitTick] = useState(0);
  const [prevTick, setPrevTick] = useState(0);
  const [skipTick, setSkipTick] = useState(0);
  const [nxplanStep, setNxplanStep] = useState(1);
  const [nxmapStep, setNxmapStep] = useState(1);
  const [pendingAction, setPendingAction] = useState(null);
  const [finishingProject, setFinishingProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasResolvedOnboard, setHasResolvedOnboard] = useState(false);

  useEffect(() => {
    let isActive = true;

    setHasResolvedOnboard(false);
    fetchOnboard?.()
      .catch((error) => {
        if (!isActive) return;

        showToast.error(
          error?.message ||
            t("Unable to load onboarding details. Please try again."),
        );
      })
      .finally(() => {
        if (!isActive) return;
        setHasResolvedOnboard(true);
      });

    return () => {
      isActive = false;
    };
  }, [fetchOnboard, project, t]);

  const handleSubmit = (action = null) => {
    setPendingAction(action);
    setSubmitTick((tick) => tick + 1);
  };

  const handleSkip = () => setSkipTick((tick) => tick + 1);

  const handleFinish = (targetProject) => {
    if (isSaving) return;
    const projectName = String(targetProject || "").toLowerCase();
    if (!projectName) return;
    setFinishingProject(projectName);
    setPendingAction(`${projectName}-finish`);
    setSubmitTick((tick) => tick + 1);
  };

  const handleFormValid = async (payload) => {
    const projectName = String(
      payload?.project || finishingProject || "",
    ).toLowerCase();
    if (!projectName) {
      setPendingAction(null);
      return;
    }

    setIsSaving(true);
    try {
      const success = await saveOnboard?.(projectName, payload);
      if (success === false) {
        throw new Error(t("Unable to save onboarding details."));
      }

      if (pendingAction === `${projectName}-visit`) {
        if (projectName === "nxmap") {
          const response = await onboardingService.getNxmapCode({
            showErrorToast: false,
          });
          const code = String(response || "").trim();
          if (!code) {
            throw new Error(t("Unable to retrieve NXMap access code."));
          }
          const base = String(config.nxmapBaseUrl || "").replace(/\/$/, "");
          const ssoUrl = `${base}/sso-login?code=${encodeURIComponent(code)}`;
          window.open(ssoUrl, "_blank", "noopener,noreferrer");
        } else {
          window.open(
            buildVisitUrl(projectName),
            "_blank",
            "noopener,noreferrer",
          );
        }
      } else {
        navigate(PATHS.DASHBOARD);
      }
    } catch (error) {
      showToast.error(
        error?.message ||
          t("Unable to save onboarding details. Please try again."),
      );
    } finally {
      setIsSaving(false);
      setFinishingProject(null);
      setPendingAction(null);
    }
  };

  const isNxmap = project === "nxmap";
  const step = isNxmap ? nxmapStep : nxplanStep;
  const projectKey = isNxmap ? "nxmap" : "nxplan";
  const visitKey = isNxmap ? "nxmap-visit" : "nxplan-visit";
  const visitLabel = isNxmap ? t("Visit NXMap") : t("Visit NXPlan");

  const isFinished = isNxmap ? step >= 4 : step >= 3;
  const isDisabled = isSaving && finishingProject === projectKey;

  const hasPermissions = (resource, actions = [], permissions = []) => {
    if (!resource || !actions.length) return false;

    return actions.some((action) =>
      permissions.includes(`${resource}:${action}`),
    );
  };

  const canUpdate = hasPermissions("onboard_setup", ["update"], permissions);
  const canCreate = hasPermissions("onboard_setup", ["create"], permissions);
  const form_disabled = isUserLevel || !(canUpdate || canCreate);

  if (!hasResolvedOnboard || loading) {
    return null;
  }
  const shouldRedirectToNotFound =
    project &&
    Object.prototype.hasOwnProperty.call(onboardData ?? {}, project) &&
    onboardData?.[project] == null;

  if (shouldRedirectToNotFound && !canCreate) {
    return <Navigate to={PATHS.NOT_FOUND} replace />;
  }

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-8rem)] lg:overflow-hidden">
      {project === "nxplan" && (
        <NxplanForm
          submitTick={submitTick}
          prevTick={prevTick}
          skipTick={skipTick}
          onValid={handleFormValid}
          setNxplanStep={setNxplanStep}
          form_disabled={form_disabled}
        />
      )}
      {project === "nxmap" && (
        <NxmapForm
          submitTick={submitTick}
          prevTick={prevTick}
          skipTick={skipTick}
          onValid={handleFormValid}
          setNxmapStep={setNxmapStep}
          form_disabled={form_disabled}
        />
      )}
      {project === "esg" && <EsgForm />}
      {!["nxmap", "nxplan", "esg"].includes(project) && (
        <div className="rounded-radius border border-border bg-card p-8 text-center text-muted-foreground">
          {t("Select a project to explore.")}
        </div>
      )}

      <div
        className={
          project !== "esg" &&
          "mt-4 flex flex-col gap-3 rounded-radius border border-border/70 bg-background/95 p-3 shadow-sm backdrop-blur lg:sticky lg:bottom-0 lg:left-0 lg:right-0 lg:mt-auto lg:grid lg:grid-cols-[auto,1fr,auto] lg:items-center lg:gap-4"
        }
      >
        <div className="w-full sm:w-auto">
          {((project === "nxplan" && nxplanStep > 1) ||
            (project === "nxmap" && nxmapStep > 1)) && (
            <Button
              variant="outline"
              onClick={() => setPrevTick((tick) => tick + 1)}
            >
              {t("Previous")}
            </Button>
          )}
        </div>
        <div className="hidden lg:block lg:justify-self-center" />
        <div className="flex w-full flex-col gap-2 justify-self-end sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
          {project !== "nxplan" && project !== "nxmap" && project !== "esg" && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="w-full sm:w-auto"
              >
                {t("Cancel")}
              </Button>
              <Button
                onClick={() =>
                  handleSubmit(project ? `${project}-visit` : null)
                }
                className="w-full sm:w-auto"
              >
                {t("Continue")}
              </Button>
            </>
          )}

          {(project === "nxplan" || project === "nxmap") && (
            <>
              {form_disabled ? (
                nxmapStep < 4 &&
                nxplanStep < 3 && (
                  <Button
                    variant="outline"
                    onClick={handleSkip}
                    className="w-full sm:w-auto"
                  >
                    {t("Next")}
                  </Button>
                )
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleSkip}
                    className="w-full sm:w-auto"
                  >
                    {t("Skip")}
                  </Button>
                  {isFinished && (
                    <Button
                      onClick={() => handleFinish(projectKey)}
                      disabled={isDisabled}
                      className="w-full sm:w-auto"
                    >
                      {isDisabled ? t("Saving...") : t("Finish")}
                    </Button>
                  )}
                  {isFinished &&
                  !(canVisitProjects && (isNxmap || isDevHost())) ? null : (
                    <Button
                      variant={isFinished ? "secondary" : "default"}
                      onClick={() => handleSubmit(isFinished ? visitKey : null)}
                      disabled={isDisabled}
                      className="w-full sm:w-auto"
                    >
                      {isFinished ? visitLabel : t("Continue")}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default OnboardingPage;
